import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getSftpSummaryStats,
  SftpSummaryStatsParams,
} from "../../../../api/getCalls/getSftpSummaryStats";
import {
  SftpSummaryStatsApiResponse,
  SftpSummaryStatsItem,
} from "../../../../data-model/sftpSummaryStatsModel";
import { SftpSummaryTableDataModel } from "../../../../data-model/sftpSummaryTableDataModel";
import { SftpSummaryTableColumnKeys } from "../../../../enums/tableColumnKeys";
import { useAbortableFetch } from "../../../../hooks/useAbortableFetch";

interface UseSftpSummaryStatsProps {
  page?: number;
  pageSize?: number;
}

interface SftpSummaryState {
  data: SftpSummaryStatsApiResponse | null;
  loading: boolean;
  showInlineLoader: boolean;
  error: Error | null;
}

interface UseSftpSummaryStatsReturn {
  tableData: SftpSummaryTableDataModel[];
  loading: boolean;
  showInlineLoader: boolean;
  error: Error | null;
  totalCount: number;
  setEndIndex: (index: number) => void;
  refetch: () => void;
}

/**
 * Transform API response items to table data format
 */
const transformResponseToTableData = (
  items: SftpSummaryStatsItem[],
): SftpSummaryTableDataModel[] => {
  return items.map((item) => ({
    [SftpSummaryTableColumnKeys.FILENAME]: item.filename,
    [SftpSummaryTableColumnKeys.APPROVED_CNT]:
      item.response_status_stats.approved_cnt,
    [SftpSummaryTableColumnKeys.NAR_CNT]: item.response_status_stats.nar_cnt,
    [SftpSummaryTableColumnKeys.DENIED_CNT]:
      item.response_status_stats.denied_cnt,
    [SftpSummaryTableColumnKeys.PENDING_CNT]:
      item.response_status_stats.pending_cnt,
    [SftpSummaryTableColumnKeys.NA_CNT]: item.response_status_stats.na_cnt,
    [SftpSummaryTableColumnKeys.NO_CLINICAL_CNT]:
      item.response_status_stats.no_clinical_cnt,
    [SftpSummaryTableColumnKeys.OTHER_CNT]:
      item.response_status_stats.other_cnt,
    [SftpSummaryTableColumnKeys.RESPONSE_TOTAL_CNT]:
      item.response_status_stats.total_cnt,
    [SftpSummaryTableColumnKeys.TEXT_SUCCESS_CNT]:
      item.text_notes_stats.success_cnt,
    [SftpSummaryTableColumnKeys.TEXT_PROGRESS_CNT]:
      item.text_notes_stats.progress_cnt,
    [SftpSummaryTableColumnKeys.TEXT_ERROR_CNT]:
      item.text_notes_stats.error_cnt,
    [SftpSummaryTableColumnKeys.TEXT_NA_CNT]: item.text_notes_stats.na_cnt,
    [SftpSummaryTableColumnKeys.TEXT_NOT_STARTED_CNT]:
      item.text_notes_stats.not_started_cnt,
    [SftpSummaryTableColumnKeys.TEXT_TOTAL_CNT]:
      item.text_notes_stats.total_cnt,
  }));
};

/**
 * Custom hook to fetch SFTP summary stats
 * Handles loading, error states, cleanup with abort controller, and pagination
 */
export const useSftpSummaryStats = ({
  page = 1,
  pageSize = 40,
}: UseSftpSummaryStatsProps = {}): UseSftpSummaryStatsReturn => {
  const [tableData, setTableData] = useState<SftpSummaryTableDataModel[]>([]);
  const [endIndex, setEndIndex] = useState<number>(0);
  const [summaryState, setSummaryState] = useState<SftpSummaryState>({
    data: null,
    loading: true,
    showInlineLoader: false,
    error: null,
  });

  const fetchStats = useCallback(
    async (signal: AbortSignal, params: SftpSummaryStatsParams) => {
      return await getSftpSummaryStats(params, signal);
    },
    [],
  );

  const { runFetch, abort } =
    useAbortableFetch<SftpSummaryStatsApiResponse>(fetchStats);

  // Initial data fetch
  useEffect(() => {
    let isMounted = true;

    const fetchInitialData = async () => {
      setSummaryState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const result = await runFetch({ page: 1, page_size: pageSize });

        // Don't update state if component is unmounted or request was aborted
        if (!isMounted) return;

        // Check if the error is an AbortError and ignore it
        if (result?.error?.name === "AbortError") {
          return;
        }

        if (result?.success && result.data?.stats) {
          const transformedData = transformResponseToTableData(
            result.data.stats,
          );
          setTableData(transformedData);
          setSummaryState({
            data: result.data,
            loading: false,
            showInlineLoader: false,
            error: null,
          });
        } else if (result?.error) {
          setSummaryState((prev) => ({
            ...prev,
            loading: false,
            error:
              result.error instanceof Error
                ? result.error
                : new Error("Unknown error"),
          }));
        } else {
          // Handle case where response is successful but has no data
          setSummaryState((prev) => ({
            ...prev,
            loading: false,
          }));
        }
      } catch (error: any) {
        // Don't update state if component is unmounted
        if (!isMounted) return;

        // Ignore AbortError
        if (error?.name === "AbortError") {
          return;
        }

        console.error("Error fetching SFTP summary stats:", error);
        setSummaryState((prev) => ({
          ...prev,
          loading: false,
          error: error instanceof Error ? error : new Error("Unknown error"),
        }));
      }
    };

    fetchInitialData();

    return () => {
      isMounted = false;
      abort();
    };
  }, [pageSize]);

  // Calculate if we should fetch more data
  const shouldFetchMoreData = useMemo(() => {
    const totalRowsLoaded = tableData.length;
    const currentPage = summaryState.data?.page ?? 1;
    const totalPages = summaryState.data?.total_pages ?? 1;

    return (
      endIndex === totalRowsLoaded - 1 &&
      currentPage < totalPages &&
      !summaryState.loading &&
      !summaryState.showInlineLoader
    );
  }, [
    endIndex,
    tableData.length,
    summaryState.data?.page,
    summaryState.data?.total_pages,
    summaryState.loading,
    summaryState.showInlineLoader,
  ]);

  // Refs to store latest values
  const setSummaryStateRef = useRef(setSummaryState);
  const setTableDataRef = useRef(setTableData);

  useEffect(() => {
    setSummaryStateRef.current = setSummaryState;
    setTableDataRef.current = setTableData;
  }, [setSummaryState, setTableData]);

  // Fetch next page
  const fetchNextPage = useCallback(async () => {
    const nextPage = (summaryState.data?.page ?? 0) + 1;

    setSummaryStateRef.current((prev) => ({
      ...prev,
      showInlineLoader: true,
    }));

    try {
      const result = await runFetch({
        page: nextPage,
        page_size: pageSize,
      });

      // Check if the error is an AbortError and ignore it
      if (result?.error?.name === "AbortError") {
        setSummaryStateRef.current((prev) => ({
          ...prev,
          showInlineLoader: false,
        }));
        return;
      }

      if (result?.success && result.data?.stats) {
        const transformedData = transformResponseToTableData(result.data.stats);

        setTableDataRef.current((prevData) => [
          ...prevData,
          ...transformedData,
        ]);
        setSummaryStateRef.current({
          data: result.data,
          loading: false,
          showInlineLoader: false,
          error: null,
        });
      }
    } catch (error: any) {
      // Ignore AbortError
      if (error?.name === "AbortError") {
        setSummaryStateRef.current((prev) => ({
          ...prev,
          showInlineLoader: false,
        }));
        return;
      }

      console.error("Error fetching next page:", error);
      setSummaryStateRef.current((prev) => ({
        ...prev,
        showInlineLoader: false,
        error: error instanceof Error ? error : new Error("Unknown error"),
      }));
    }
  }, [summaryState.data?.page, pageSize, runFetch]);

  // Trigger fetch when needed
  useEffect(() => {
    if (shouldFetchMoreData) {
      fetchNextPage();
    }
  }, [shouldFetchMoreData, fetchNextPage]);

  const refetch = useCallback(() => {
    setTableData([]);
    setSummaryState({
      data: null,
      loading: true,
      showInlineLoader: false,
      error: null,
    });
    runFetch({ page: 1, page_size: pageSize });
  }, [runFetch, pageSize]);

  return {
    tableData,
    loading: summaryState.loading,
    showInlineLoader: summaryState.showInlineLoader,
    error: summaryState.error,
    totalCount: summaryState.data?.total_count ?? 0,
    setEndIndex,
    refetch,
  };
};
